import {BaseService} from "./baseService";
import {Event} from "./types";

export interface UpsertEventResponse {
    message: string;
    eventKey: string;
}

export class EventService extends BaseService {
    /**
     * Get all events
     */
    static async getEvents(): Promise<Event[]> {
        try {
        const response = await this.get("/events");
            return await this.handleResponse<Event[]>(response);
        } catch (error) {
            console.error("Error fetching events:", error);
            throw error;
        }
    }

    /**
     * Fetch a single event by ID
     */
    static async getEvent(id: string): Promise<Event> {
        try {
            const response = await this.get(`/events/${id}`);
            return await this.handleResponse<Event>(response);
        } catch (error) {
            console.error(`Error fetching event ${id}:`, error);
            throw error;
        }
    }

    /**
     * Upsert an event (create or update)
     */
    static async upsertEvent(eventData: Partial<Event>): Promise<UpsertEventResponse> {
        const response = await this.makeRequest("/events", {
            method: "PUT",
            body: JSON.stringify(eventData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || `Failed to upsert event: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Validate event data before submission
     */
    static validateEvent(eventData: Partial<Event>): string[] {
        const errors: string[] = [];

        if (!eventData.eventKey?.trim()) {
            errors.push("Event Key is required");
        }

        if (!eventData.description?.trim()) {
            errors.push("Description is required");
        }

        if (!eventData.imageUrl?.trim()) {
            errors.push("Image URL is required");
        }

        if (!eventData.status?.trim()) {
            errors.push("Status is required");
        }

        // Validate datetime formats if provided
        if (eventData.scheduledStartDatetime && isNaN(Date.parse(eventData.scheduledStartDatetime))) {
            errors.push("Invalid start date/time format");
        }

        if (eventData.scheduledEndDatetime && isNaN(Date.parse(eventData.scheduledEndDatetime))) {
            errors.push("Invalid end date/time format");
        }

        // Validate URL format
        if (eventData.imageUrl) {
            try {
                new URL(eventData.imageUrl);
            } catch {
                errors.push("Invalid image URL format");
            }
        }

        return errors;
    }

    /**
     * Format date for datetime-local input
     */
    static formatDateForInput(dateString: string | null): string {
        if (!dateString) return "";

        try {
            const date = new Date(dateString);
            // Format as YYYY-MM-DDTHH:MM for datetime-local input
            return date.toISOString().slice(0, 16);
        } catch {
            return "";
        }
    }

    /**
     * Get available event statuses
     */
    static getEventStatuses(): string[] {
        return ["DRAFT", "SCHEDULED", "LIVE", "FINISHED", "CANCELLED"];
    }
}